import React from "react";
import { DailyBalance as DailyBalanceType } from "../../../types";
import { PaymentMethodBalanceIcon } from "../../../utils/paymentMethodIcons";
import { Icons } from "../../shared/Icons";
import {
  BALANCE_PAYMENT_METHOD_BAR_COLORS,
  BALANCE_PAYMENT_METHOD_LABELS,
  formatBalanceCurrency,
} from "./balanceShared";

export interface BalanceBreakdownProps {
  balance: DailyBalanceType;
  totalPrimaryLabel: string;
  emptyPeriodMessage: string;
  unpaidCount: number;
  unpaidRows: React.ReactNode;
  showAllPaidMessage: boolean;
}

export const BalanceBreakdown: React.FC<BalanceBreakdownProps> = ({
  balance,
  totalPrimaryLabel,
  emptyPeriodMessage,
  unpaidCount,
  unpaidRows,
  showAllPaidMessage,
}) => (
  <div className="space-y-4 sm:space-y-6">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 rounded-lg p-4 sm:p-6 border border-blue-200 dark:border-blue-800">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-400 mb-1">{totalPrimaryLabel}</p>
            <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
              {formatBalanceCurrency(balance.total)}
            </p>
          </div>
          <div className="flex items-center">
            <Icons.Money sx={{ fontSize: 36 }} className="text-gray-600 dark:text-slate-400" />
          </div>
        </div>
      </div>

      {balance.totalWithoutPayment !== undefined && (
        <div className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-950 dark:to-red-950 rounded-lg p-4 sm:p-6 border border-orange-200 dark:border-orange-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-400 mb-1">Sem Pagamento</p>
              <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-orange-700 dark:text-orange-400">
                {formatBalanceCurrency(balance.totalWithoutPayment)}
              </p>
            </div>
            <div className="flex items-center">
              <Icons.Warning sx={{ fontSize: 36 }} className="text-orange-600 dark:text-orange-400" />
            </div>
          </div>
        </div>
      )}
    </div>

    <div>
      <h4 className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-slate-300 mb-3 sm:mb-4">
        Por Método de Pagamento
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {(Object.keys(balance.byPaymentMethod) as Array<keyof DailyBalanceType["byPaymentMethod"]>).map(
          (method) => {
            const amount = balance.byPaymentMethod[method];
            const percentage = balance.total > 0 ? (amount / balance.total) * 100 : 0;

            return (
              <div
                key={method}
                className="bg-gray-50 dark:!bg-slate-900 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-slate-600 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="flex shrink-0 items-center justify-center [&>.MuiSvgIcon-root]:block">
                      <PaymentMethodBalanceIcon method={method} size={24} />
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-slate-300 truncate">
                      {BALANCE_PAYMENT_METHOD_LABELS[method]}
                    </span>
                  </div>
                  <span className="text-sm sm:text-base font-bold text-gray-900 dark:text-white">
                    {formatBalanceCurrency(amount)}
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
                  <div
                    className={`${BALANCE_PAYMENT_METHOD_BAR_COLORS[method]} h-2 rounded-full transition-all duration-300`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">{percentage.toFixed(1)}% do total</p>
              </div>
            );
          }
        )}
      </div>
    </div>

    {balance.total === 0 && (
      <div className="bg-yellow-50 dark:bg-yellow-950/40 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 sm:p-4">
        <p className="text-xs sm:text-sm text-yellow-800 dark:text-yellow-200 text-center">{emptyPeriodMessage}</p>
      </div>
    )}

    {unpaidCount > 0 && (
      <div>
        <h4 className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-slate-300 mb-3 sm:mb-4">
          Agendamentos sem Pagamento ({unpaidCount})
        </h4>
        <div className="space-y-2 max-h-64 overflow-y-auto">{unpaidRows}</div>
      </div>
    )}

    {showAllPaidMessage && (
      <div className="bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-800 rounded-lg p-3 sm:p-4">
        <p className="text-xs sm:text-sm text-green-800 dark:text-green-200 text-center flex items-center justify-center gap-2">
          <Icons.Check sx={{ fontSize: 18 }} className="text-green-800 dark:text-green-300" />
          Todos os agendamentos têm pagamento registrado
        </p>
      </div>
    )}
  </div>
);
